import Alert from '@mui/material/Alert';
import Autocomplete from '@mui/material/Autocomplete';
import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import { LOCATION_TYPE_LABEL_HU, LocationType } from '@persped/shared';
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useLocations, usePrintLocationLabels } from '../../api/queries';
import { ProblemAlert } from '../../components/ProblemAlert';

const MAX_BARCODES = 200;

/**
 * Tömeges kijelölés CÍMKETÍPUS szerint (08-13 CR 9.3).
 *
 * A gombok a NÉGY címketípushoz igazodnak, nem a hat lokációtípushoz: a
 * `LabelKind.LOCATION` egyetlen sablon, ami a raklapon és a polcon KÍVÜLI
 * minden tárolási típust lefed (Előkészítő, Munkaállomás, Tároló, Egyéb).
 * Ezért a harmadik gomb kizárásos, nem felsoroló — így egy jövőbeli új
 * lokációtípus sem esik ki csendben a kijelölésből.
 */
const GYORSVALASZTOK: ReadonlyArray<{ label: string; matches: (t: LocationType) => boolean }> = [
  {
    label: `Minden ${LOCATION_TYPE_LABEL_HU[LocationType.PALLET].toLowerCase()}`,
    matches: (t) => t === LocationType.PALLET,
  },
  {
    label: `Minden ${LOCATION_TYPE_LABEL_HU[LocationType.SHELF].toLowerCase()}`,
    matches: (t) => t === LocationType.SHELF,
  },
  {
    label: 'Minden egyéb lokáció',
    matches: (t) => t !== LocationType.PALLET && t !== LocationType.SHELF,
  },
];

/**
 * Lokáció- és raklapcímkék nyomtatása.
 *
 * Ez a mappacímke FORDÍTOTTJA: itt a `location` sorok MÁR léteznek, tehát
 * nincs sorszám-kiosztás — csak nyomtatás. Ezért választani lehet a
 * meglévő lokációk közül, nem darabszámot megadni.
 */
export function NewLocationBatchDialog({ open, onClose }: { open: boolean; onClose: () => void }) {
  const navigate = useNavigate();
  const print = usePrintLocationLabels();
  const locations = useLocations({});
  const [selected, setSelected] = useState<string[]>([]);
  const [note, setNote] = useState('');

  const valid = selected.length > 0 && selected.length <= MAX_BARCODES;

  const close = () => {
    print.reset();
    setSelected([]);
    setNote('');
    onClose();
  };

  return (
    <Dialog open={open} onClose={close} maxWidth="sm" fullWidth>
      <DialogTitle>Lokáció-, raklap- és polccímkék nyomtatása</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ mt: 1 }}>
          <Alert severity="info">
            Ezek a lokációk már léteznek a rendszerben, ezért itt nincs sorszám-foglalás — csak
            nyomtatás. Egy elrontott nyomtatás bármikor megismételhető.
          </Alert>

          <Autocomplete
            multiple
            options={(locations.data ?? []).map((l) => l.barcode)}
            value={selected}
            onChange={(_, next) => setSelected(next.slice(0, MAX_BARCODES))}
            loading={locations.isPending}
            getOptionLabel={(barcode) => {
              const loc = locations.data?.find((l) => l.barcode === barcode);
              return loc ? `${loc.barcode} — ${loc.name}` : barcode;
            }}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Lokációk"
                helperText={`Legfeljebb ${MAX_BARCODES} db. Kiválasztva: ${selected.length}`}
              />
            )}
          />

          {/*
            Típusonkénti gyorsgombok (08-13 CR 9.3). A választóban minden
            lokáció ott van, tehát a polccímke eddig is nyomtatható volt —
            ezek csak a tömeges kijelölést rövidítik le.
          */}
          <Stack direction="row" spacing={1} sx={{ flexWrap: 'wrap' }}>
            {GYORSVALASZTOK.map((gy) => (
              <Button
                key={gy.label}
                size="small"
                onClick={() =>
                  setSelected(
                    (locations.data ?? [])
                      .filter((l) => gy.matches(l.locationType))
                      .map((l) => l.barcode)
                      .slice(0, MAX_BARCODES),
                  )
                }
              >
                {gy.label}
              </Button>
            ))}
            <Button size="small" onClick={() => setSelected([])}>
              Ürítés
            </Button>
          </Stack>

          <TextField
            label="Megjegyzés (opcionális)"
            value={note}
            onChange={(e) => setNote(e.target.value)}
            fullWidth
          />

          {locations.isError && <ProblemAlert error={locations.error} />}
          {print.isError && <ProblemAlert error={print.error} />}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={close}>Mégse</Button>
        <Button
          variant="contained"
          disabled={!valid || print.isPending}
          onClick={() => {
            print.mutate(
              { barcodes: selected, note: note.trim() || undefined },
              {
                onSuccess: (batch) => {
                  close();
                  void navigate(`/cimkek/${batch.id}`);
                },
              },
            );
          }}
        >
          Nyomtatás
        </Button>
      </DialogActions>
    </Dialog>
  );
}
